import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../../App';
import { Building2, Calendar, List, MapPin, Clock, Users, Search, Filter, ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { formatTimeRange } from '../../utils/dateTime';
import CalendarView from '../../components/CalendarView';

const PartnerCommunityOpportunities = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [search, setSearch] = useState('');

  const fetchOpportunities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/community-opportunities`);
      setOpportunities(res.data || []);
    } catch (err) {
      toast.error('Failed to load community opportunities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const now = new Date();
  const userOrgId = user?.partner_org_id;

  const filteredOpps = opportunities.filter(opp => {
    const oppDate = parseISO(opp.date);
    if (timeFilter === 'upcoming' && oppDate < now) return false;
    if (timeFilter === 'past' && oppDate >= now) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        opp.title?.toLowerCase().includes(q) ||
        opp.location?.toLowerCase().includes(q) ||
        opp.partner_org_name?.toLowerCase().includes(q) ||
        opp.description?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const isOwnOrg = (opp) => opp.partner_org_id === userOrgId;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4EA] flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-community-opportunities-page">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#8B2E3C]" />
              <span className="font-bold text-[#222]">{user?.partner_org_name || 'Partner Portal'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm overflow-x-auto">
              <Link to="/partner/dashboard" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#8B2E3C] font-bold whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#222] flex items-center gap-2">
              <Globe className="w-7 h-7 text-[#8B2E3C]" />
              Community Opportunity Board
            </h1>
            <p className="text-sm text-[#666] mt-1">Browse all volunteer opportunities across the community</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-[#8B2E3C] text-white' : 'bg-white border border-[#E9DDCF] text-[#666] hover:border-[#8B2E3C]'}`}
              data-testid="view-mode-list"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'calendar' ? 'bg-[#8B2E3C] text-white' : 'bg-white border border-[#E9DDCF] text-[#666] hover:border-[#8B2E3C]'}`}
              data-testid="view-mode-calendar"
            >
              <Calendar className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, location, or organization..."
              className="input-field pl-10 w-full"
              data-testid="community-search"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'past', label: 'Past' },
              { key: 'all', label: 'All' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setTimeFilter(f.key)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  timeFilter === f.key
                    ? 'bg-[#8B2E3C] text-white'
                    : 'bg-white border border-[#E9DDCF] text-[#666] hover:border-[#8B2E3C]'
                }`}
                data-testid={`time-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <CalendarView opportunities={filteredOpps} basePath="/opportunity" />
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {filteredOpps.length === 0 ? (
              <div className="card text-center py-12 text-[#666]" data-testid="empty-state">
                <Globe className="w-12 h-12 mx-auto mb-3 text-[#ccc]" />
                <p className="text-lg font-medium mb-1">No opportunities found</p>
                <p className="text-sm">
                  {search ? 'Try adjusting your search.' : timeFilter === 'upcoming' ? 'No upcoming opportunities right now.' : 'No past opportunities found.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOpps.map(opp => {
                  const oppDate = parseISO(opp.date);
                  const isPast = oppDate < now;
                  const isFull = opp.signups_count >= opp.capacity;
                  const isClosed = opp.status === 'closed';
                  const own = isOwnOrg(opp);

                  return (
                    <div
                      key={opp.id}
                      className={`card hover:shadow-md transition-shadow cursor-pointer ${own ? 'border-l-4 border-l-[#C9A227]' : ''}`}
                      onClick={() => navigate(`/opportunity/${opp.id}`)}
                      data-testid={`community-opp-${opp.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-[#222] text-lg truncate">{opp.title}</h3>
                            {own && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#C9A227]/15 text-[#C9A227] font-medium border border-[#C9A227]/30" data-testid={`own-badge-${opp.id}`}>
                                Your Org
                              </span>
                            )}
                            {isClosed && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">Closed</span>
                            )}
                            {isPast && !isClosed && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Past</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-[#666]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {format(oppDate, 'MMM d, yyyy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {formatTimeRange(opp.start_time, opp.end_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {opp.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {opp.signups_count}/{opp.capacity}
                              {isFull && <span className="text-red-600 font-medium ml-1">Full</span>}
                            </span>
                          </div>
                          {opp.partner_org_name && (
                            <div className="flex items-center gap-1 text-xs text-[#888] mt-1">
                              <Building2 className="w-3 h-3" />
                              {opp.partner_org_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Stats bar */}
        <div className="mt-6 text-center text-sm text-[#888]" data-testid="results-count">
          {filteredOpps.length} opportunit{filteredOpps.length !== 1 ? 'ies' : 'y'} shown
          {search && ` matching "${search}"`}
        </div>
      </div>
    </div>
  );
};

export default PartnerCommunityOpportunities;
